<template>
  <section class="px-6 mt-6" :aria-busy="loading || saving">
    <template v-if="loading">
      <p class="sr-only" role="status">{{ $t('projects.loading_blocks') }}</p>
      <blocks-loading-skeleton></blocks-loading-skeleton>
    </template>

    <div v-else-if="loadError" class="py-8 px-4 text-center border border-gray-200 rounded-md" role="alert">
      <p class="text-sm text-gray-700">{{ $t('projects.blocks_load_error') }}</p>
      <button type="button" v-on:click="loadBlocks"
              class="mt-3 inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-bpblue">
        {{ $t('projects.retry') }}
      </button>
    </div>

    <template v-else>
      <div v-if="!blocks.length" class="py-8 px-4 border-2 border-dashed border-gray-300 rounded-md">
        <h3 class="text-center text-sm font-semibold text-gray-900">{{ $t('projects.empty_blocks_title') }}</h3>
        <p class="mt-1 text-center text-sm text-gray-600">{{ $t('projects.empty_blocks_text') }}</p>
        <ul class="mt-4 grid gap-3 sm:grid-cols-2" :aria-label="$t('projects.add_first_block')">
          <li v-for="(blockType, type) in blockTypes" :key="type">
            <button type="button" v-on:click="insertBlock(type, 0)" :disabled="saving"
                    class="flex h-full w-full items-start gap-3 rounded-md border border-gray-200 bg-white p-3 text-left hover:border-bpblue disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-bpblue">
              <font-awesome-icon :icon="blockType.icon" class="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-500" aria-hidden="true"/>
              <span>
                <span class="block text-sm font-medium text-gray-900">{{ $t(blockType.labelKey) }}</span>
                <span class="block text-xs text-gray-600">{{ $t(blockType.descriptionKey) }}</span>
              </span>
            </button>
          </li>
        </ul>
      </div>

      <vue-draggable
        v-model="blocks"
        handle=".block-drag-handle"
        ghost-class="drag-ghost"
        chosen-class="drag-chosen"
        :animation="200"
        :disabled="saving"
      >
        <lesson-block-card v-for="(block, index) in blocks" :key="block.key"
                           :block="block" :block-types="blockTypes" :index="index" :total="blocks.length"
                           :lesson-uuid="lessonUuid" :saving="saving" :error="errors[block.key] ?? null"
                           :collapsed="collapsedKeys.includes(block.key)"
                           @update:data="updateBlockData(block, $event)"
                           @move="moveBlock(index, $event)"
                           @duplicate="duplicateBlock(index)"
                           @remove="removeBlock(index)"
                           @toggle-collapse="toggleCollapse(block.key)"
                           @insert-after="insertBlock($event, index + 1)"></lesson-block-card>
      </vue-draggable>

      <div v-if="blocks.length">
        <block-insert-menu :block-types="blockTypes" :label="$t('projects.add_block')" :disabled="saving"
                           @select="insertBlock($event, blocks.length)"></block-insert-menu>
      </div>

      <blocks-save-bar :is-dirty="isDirty" :saving="saving" :saved-at="savedAt" :error-count="errorCount"
                       :conflict="conflict"
                       @save="save" @discard="askDiscard" @go-to-first-error="goToFirstError"
                       @resolve-conflict="isConflictConfirmationOpen = true"></blocks-save-bar>
    </template>

    <modal-component v-if="isDiscardConfirmationOpen" :on-process="confirmDiscard" :on-cancel="cancelDiscard"
                     on-process-title="projects.discard_changes" on-cancel-title="general.unsaved_changes_stay">
      <template v-slot:header>{{ $t('projects.discard_confirm_title') }}</template>
      {{ $t('projects.discard_confirm_text') }}
    </modal-component>

    <modal-component v-if="quizBlockToRemove" :on-process="confirmRemoveQuizBlock" :on-cancel="cancelRemoveQuizBlock"
                     on-process-title="projects.delete_block">
      <template v-slot:header>{{ $t('projects.quiz_remove_block_title') }}</template>
      {{ $t('projects.quiz_remove_block_text') }}
    </modal-component>

    <modal-component v-if="isConflictConfirmationOpen" :on-process="confirmReload" :on-cancel="keepEditing"
                     on-process-title="projects.blocks_conflict_reload" on-cancel-title="projects.blocks_conflict_keep">
      <template v-slot:header>{{ $t('projects.blocks_conflict_title') }}</template>
      {{ conflictMessage }} {{ $t('projects.blocks_conflict_text') }}
    </modal-component>
  </section>
</template>

<script>
import { VueDraggable } from 'vue-draggable-plus';
import LessonBlockService from "@/services/project/LessonBlockService";
import QuizService from "@/services/project/QuizService";
import NotificationService from "@/services/notification/NotificationService";
import blockTypes from "@/views/projects/ProjectEdit/Blocks/blockTypes";
import BlocksSaveBar from "@/views/projects/ProjectEdit/Blocks/BlocksSaveBar";
import BlocksLoadingSkeleton from "@/views/projects/ProjectEdit/Blocks/BlocksLoadingSkeleton";
import ModalComponent from "@/components/modals/ModalComponent";
import LessonBlockCard from "@/views/projects/ProjectEdit/Blocks/LessonBlockCard";
import BlockInsertMenu from "@/views/projects/ProjectEdit/Blocks/BlockInsertMenu";
import {firstMessage, groupBlockErrors} from "@/views/projects/ProjectEdit/Blocks/blockErrors";

const clone = (value) => JSON.parse(JSON.stringify(value))
// Lo que se compara para saber si hay cambios: orden, tipo y datos de cada bloque.
const serialize = (blocks) => JSON.stringify(blocks.map(({uuid, type, data}) => ({uuid, type, data})))
// uuids de los quizzes a los que apuntan los bloques de quiz.
const quizIdsOf = (blocks) => blocks.filter((block) => block.type === 'quiz' && block.data?.quiz_id).map((block) => block.data.quiz_id)

let newBlockCounter = 0

/*
 * Editor de una lección compuesta por bloques ordenables (FE-030, API de LMS-074).
 * Los cambios se aplican en local y se guardan de forma explícita con «Guardar»,
 * en una sola petición con toda la lista (FE-055, LMS-218).
 */
export default {
  name: "LessonBlocksEditor",
  components: {
    VueDraggable,
    BlocksSaveBar,
    BlocksLoadingSkeleton,
    ModalComponent,
    LessonBlockCard,
    BlockInsertMenu
  },
  props: {
    lessonUuid: {
      type: String,
      required: true
    }
  },
  emits: ['dirty-change'],
  data() {
    return {
      blockTypes,
      blocks: [],
      // Último estado guardado en el servidor, para detectar cambios y descartarlos.
      savedBlocks: [],
      // Bloques plegados en esta sesión (solo vista: no se guarda).
      collapsedKeys: [],
      errors: {},
      loading: false,
      loadError: false,
      saving: false,
      savedAt: null,
      isDiscardConfirmationOpen: false,
      // Huella de la última carga o guardado (LMS-218): el backend responde 409 si ya no coincide.
      version: null,
      conflict: false,
      conflictMessage: '',
      isConflictConfirmationOpen: false,
      // Quizzes de esta lección que conoce el editor: los cargados y los creados desde un
      // bloque (se crean en el servidor antes de guardar). Sirve para borrar los que se quedan
      // sin bloque al guardar o descartar (FE-067).
      knownQuizIds: [],
      // Clave del bloque de quiz guardado que se va a quitar, pendiente de confirmar.
      quizBlockToRemove: null,
    }
  },
  computed: {
    isDirty() {
      return serialize(this.blocks) !== serialize(this.savedBlocks)
    },
    errorCount() {
      return Object.keys(this.errors).length
    }
  },
  watch: {
    isDirty(isDirty) {
      this.$emit('dirty-change', isDirty)
    },
    lessonUuid() {
      this.loadBlocks()
    }
  },
  created() {
    this.loadBlocks()
  },
  mounted() {
    window.addEventListener('keydown', this.onKeydown)
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.onKeydown)
  },
  methods: {
    loadBlocks() {
      this.loading = true
      this.loadError = false
      this.savedAt = null
      this.conflict = false
      LessonBlockService.index(this.lessonUuid)
          .then((response) => {
            const blocks = response.data?.data ?? []
            this.version = response.data?.meta?.version ?? null
            this.setSavedBlocks(blocks
                .slice()
                .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
                .map((block) => {
                  const loaded = {key: block.uuid, uuid: block.uuid, type: block.type, data: block.data ?? {}}
                  return {...loaded, data: this.withServerData(loaded, block)}
                }))
            this.knownQuizIds = quizIdsOf(this.savedBlocks)
          })
          .catch(() => {
            this.loadError = true
          })
          .finally(() => {
            this.loading = false
          })
    },
    setSavedBlocks(blocks) {
      this.savedBlocks = clone(blocks)
      this.blocks = clone(blocks)
      this.errors = {}
    },
    blockLabel(type) {
      const blockType = this.blockTypes[type]
      return blockType ? this.$t(blockType.labelKey) : type
    },
    newBlock(type, data) {
      newBlockCounter += 1
      return {key: 'new-' + newBlockCounter, uuid: null, type, data}
    },
    insertBlock(type, position) {
      const blockType = this.blockTypes[type]
      if (!blockType) return

      const block = this.newBlock(type, blockType.createData())
      this.blocks.splice(position, 0, block)
      this.focusBlock(block)
    },
    duplicateBlock(index) {
      const original = this.blocks[index]
      if (this.blockTypes[original.type]?.duplicable === false) return

      const copy = this.newBlock(original.type, clone(original.data))
      this.blocks.splice(index + 1, 0, copy)
      this.focusBlock(copy)
    },
    updateBlockData(block, data) {
      block.data = data
      if (block.type === 'quiz' && data?.quiz_id && !this.knownQuizIds.includes(data.quiz_id)) {
        this.knownQuizIds.push(data.quiz_id)
      }
    },
    removeBlock(index) {
      // Quitar un quiz ya guardado borra, al guardar, sus preguntas y los intentos de los alumnos: se confirma antes.
      const block = this.blocks[index]
      if (block.type === 'quiz' && quizIdsOf(this.savedBlocks).includes(block.data?.quiz_id)) {
        this.quizBlockToRemove = block.key
        return
      }
      this.spliceBlock(index)
    },
    confirmRemoveQuizBlock() {
      const index = this.blocks.findIndex((block) => block.key === this.quizBlockToRemove)
      this.quizBlockToRemove = null
      if (index !== -1) this.spliceBlock(index)
    },
    cancelRemoveQuizBlock() {
      this.quizBlockToRemove = null
    },
    spliceBlock(index) {
      const [block] = this.blocks.splice(index, 1)
      this.collapsedKeys = this.collapsedKeys.filter((key) => key !== block.key)
      const errors = {...this.errors}
      delete errors[block.key]
      this.errors = errors
    },
    moveBlock(index, target) {
      if (target < 0 || target >= this.blocks.length || target === index) return

      const [block] = this.blocks.splice(index, 1)
      this.blocks.splice(target, 0, block)
      this.focusBlock(block)
    },
    toggleCollapse(key) {
      this.collapsedKeys = this.collapsedKeys.includes(key)
          ? this.collapsedKeys.filter((collapsedKey) => collapsedKey !== key)
          : [...this.collapsedKeys, key]
    },
    // Tras insertar o mover, el foco sigue al bloque para no perderse con teclado.
    focusBlock(block, scroll = true) {
      this.$nextTick(() => {
        const element = document.getElementById('block-card-' + block.key)
        if (!element) return
        if (scroll) {
          const smooth = !window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
          element.scrollIntoView({behavior: smooth ? 'smooth' : 'auto', block: 'nearest'})
        }
        element.focus({preventScroll: true})
      })
    },
    // Cmd/Ctrl+S guarda en vez de abrir el «Guardar página» del navegador.
    onKeydown(event) {
      if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== 's') return
      event.preventDefault()
      if (!this.loading && !this.saving && this.isDirty && !this.isDiscardConfirmationOpen) this.save()
    },
    askDiscard() {
      if (this.isDirty) this.isDiscardConfirmationOpen = true
    },
    cancelDiscard() {
      this.isDiscardConfirmationOpen = false
    },
    confirmDiscard() {
      this.isDiscardConfirmationOpen = false
      this.blocks = clone(this.savedBlocks)
      this.errors = {}
      // Los quizzes creados en bloques que no se llegaron a guardar no los usa nadie.
      this.deleteOrphanQuizzes()
    },
    // Lleva al primer bloque con error (en el orden de la lección), lo despliega y le da el foco.
    goToFirstError() {
      const block = this.blocks.find((candidate) => this.errors[candidate.key])
      if (!block) return

      this.collapsedKeys = this.collapsedKeys.filter((key) => key !== block.key)
      this.focusBlock(block)
    },
    validate() {
      this.errors = {}
      this.blocks.forEach((block) => {
        const error = this.blockTypes[block.type]?.validate(block.data)
        if (error) this.errors[block.key] = {key: error}
      })
      return Object.keys(this.errors).length === 0
    },
    async save() {
      if (this.conflict) return
      if (!this.validate()) {
        this.goToFirstError()
        return
      }

      this.saving = true
      // Lo que se envía: si se sigue escribiendo mientras se guarda, eso queda como cambio pendiente.
      const sent = clone(this.blocks)
      try {
        const response = await LessonBlockService.sync(this.lessonUuid, sent.map((block) => this.toPayload(block)), this.version)
        this.applySaved(sent, response.data?.data ?? [])
        this.version = response.data?.meta?.version ?? null
        this.savedAt = new Date()
        NotificationService.showNotification(this.$t('projects.blocks_saved'), "success")
        // Después del guardado, no antes: si el lote falla, los bloques quitados siguen apuntando a su quiz.
        this.deleteOrphanQuizzes()
      } catch (error) {
        if (error?.response?.status === 409) {
          this.openConflict(error)
        } else if (this.applyServerErrors(error)) {
          this.goToFirstError()
        } else {
          NotificationService.showApiError(error, this.$t('projects.blocks_save_error'))
        }
      } finally {
        this.saving = false
      }
    },
    /*
     * La respuesta trae la lista en el mismo orden que se envió (mientras se
     * guarda no se puede mover, añadir ni quitar bloques): el bloque N recibe
     * su uuid y lo que calcule el backend.
     */
    applySaved(sent, saved) {
      sent.forEach((block, index) => {
        const server = saved[index]
        if (!server) return

        block.uuid = server.uuid
        block.data = this.withServerData(block, server)
        const current = this.blocks[index]
        current.uuid = server.uuid
        current.data = this.withServerData(current, server)
      })
      this.savedBlocks = sent
    },
    // Un 409 es que otra persona ha guardado la lección: no se pierde nada hasta que se decida recargar.
    openConflict(error) {
      this.conflict = true
      this.conflictMessage = error.response.data?.message || this.$t('projects.blocks_conflict_fallback')
      this.isConflictConfirmationOpen = true
    },
    confirmReload() {
      this.isConflictConfirmationOpen = false
      this.loadBlocks()
    },
    keepEditing() {
      this.isConflictConfirmationOpen = false
    },
    /*
     * Un 422 trae los errores como `blocks.N.data.*`: se pintan bajo el bloque N,
     * con el texto del tipo si sabe traducirlo (p. ej. dominio no permitido en
     * `embed`) o con el mensaje del backend.
     */
    applyServerErrors(error) {
      const errors = error?.response?.status === 422 ? error.response.data?.errors : null
      const errorsByIndex = groupBlockErrors(errors)
      const blockErrors = {}

      Object.entries(errorsByIndex).forEach(([index, fieldErrors]) => {
        const block = this.blocks[index]
        if (!block) return

        blockErrors[block.key] = this.blockTypes[block.type]?.serverError?.(fieldErrors, block.data)
            ?? {message: firstMessage(fieldErrors)}
      })
      if (!Object.keys(blockErrors).length) return false

      this.errors = blockErrors
      return true
    },
    /*
     * Borra los quizzes que ya no usa ningún bloque, ni guardado ni en edición: los
     * de bloques quitados y guardados y los creados en bloques descartados (FE-067).
     * El backend no los borra al guardar el lote. Un fallo no bloquea: se avisa y se
     * reintenta en el siguiente guardado; un 404 es que ya no existía.
     */
    async deleteOrphanQuizzes() {
      const inUse = new Set([...quizIdsOf(this.savedBlocks), ...quizIdsOf(this.blocks)])
      const orphans = this.knownQuizIds.filter((quizId) => !inUse.has(quizId))
      if (!orphans.length) return

      const lessonUuid = this.lessonUuid
      const results = await Promise.allSettled(orphans.map((quizId) => QuizService.destroy(quizId)))
      const failed = orphans.filter((quizId, index) => results[index].status === 'rejected'
          && results[index].reason?.response?.status !== 404)
      // Si entretanto se ha cambiado de lección, `knownQuizIds` ya es de la otra.
      if (lessonUuid !== this.lessonUuid) return

      this.knownQuizIds = this.knownQuizIds.filter((quizId) => !orphans.includes(quizId) || failed.includes(quizId))
      if (failed.length) NotificationService.showNotification(this.$t('projects.quiz_orphan_delete_error'), "error")
    },
    // Mezcla en `data` lo que devuelve el backend (p. ej. la vista previa de una imagen).
    withServerData(block, server) {
      const fromResponse = this.blockTypes[block.type]?.fromResponse
      return fromResponse ? fromResponse(block.data, server) : block.data
    },
    toPayload(block) {
      const toPayload = this.blockTypes[block.type]?.toPayload
      return {...block, data: toPayload ? toPayload(block.data) : block.data}
    },
  }
}
</script>

<style scoped>
/* Mismo aspecto que al arrastrar en el currículo (ProjectContentComponent). */
:deep(.drag-ghost) {
  opacity: 0.4;
  background: #eff6ff;
  outline: 2px dashed #93c5fd;
  outline-offset: -2px;
}

:deep(.drag-chosen) {
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
}
</style>
